// @flow
import React, { PureComponent } from "react";
import { connect, Dispatch } from "react-redux";
import { compose } from "redux";
import { fetchForm, fetchNewForm, saveForm, validateForm } from "data/api";
import { togglesUpdateCounterAction } from "data/actions";
import {
    RefreshMode,
    ServerEventActionType,
    ServerEventItemType,
    type ServerEventMessage,
    type NewFormTypeEnum,
    NewFormType,
    type FormResultDTO,
    type DocFormResultDTO,
    type ApiError,
} from "data/types";
import { getLocalizedText } from "data/utils";
import Form from "components/Form";
import FormEdit from "components/FormEdit";
import * as s from "data/reducers/selectors";
import Constants, {
    type ConstantsEnum,
    isFeatureOn,
    Feature,
} from "data/constants";
import Status from "components/Status";
import loc from "i18next";
import { toastActionResult } from "data/toast";
import isEmpty from "lodash/isEmpty";
import GlobalEventsHandler from "containers/GlobalEventsHandler";
import { openConflictModal } from "components/ConflictModal";
import { produce } from "immer";
import withOfflineHandler from "containers/OfflineHandler";
import withRememberValues from "containers/RememberValues";
import withWebDavName, { type WebDavNameProps } from "containers/WebDavName";
import withApplyToAll, { type ApplyToAllProps } from "containers/ApplyToAll";
import FormBarFactory from "components/FormBar/Factory";
import styles from "./Form.module.css";
import { ApiConflictError, ApiForbiddenError } from "data/apiError";

type Props = {
    form?: FormResultDTO | DocFormResultDTO,
    /** the current itemUri */
    itemUri: string,
    /** the current viewName */
    viewName: string,
    /** whether we are now editing */
    isEditMode: boolean,
    /** callback when cancelling edit mode */
    onCancel: () => void,
    /** callback when saving a new item
     * @async
     * @param {object} model the form's values
     * @param {boolean?} keepOriginalFilename whether to use the actual filename or the user input
     * @param {number?} formatId whether to use the actual filename or the user input
     * @param {boolean?} applyToAll whether to use the actual filename or the user input
     * @returns {boolean} true if success, false otherwise
     */
    onSaveNew: (
        model: Object,
        keepOriginalFilename?: boolean,
        formatId: number,
        applyToAll?: boolean
    ) => Promise<boolean>,
    /** callback when the form definition has been loaded from server
     * @param {FormResultDTO | DocFormResultDTO} form the form definition
     */
    onFormLoad: (form: FormResultDTO | DocFormResultDTO) => void,
    /** which new item to create (Folder or Document) */
    newFormType: NewFormTypeEnum,
    /** pass-through save button label */
    saveLabel?: string,
    /** pass-through saving label */
    savingLabel?: string,
    /** pass-through to FormBarFactory */
    defaultFormTitle?: string,
    /** pass-through to FormBarFactory */
    defaultFormIcon?: string,
    /** whether to enable save form button */
    isFormButtonEnabled?: boolean,
    /** whether an internet connection is detected */
    isOnline?: boolen,
    /** whether offline feature is enabled */
    isOfflineAllowed?: boolean,
    /** whether to return ALL form fields or only changeLog */
    onSaveReturnAllFields?: boolean,
    /** defaultValues to preset on the form, accepts any {field:value} or {attribute:value} (no prefixes) */
    defaultValues?: Object,
    /** Redux dispatch */
    dispatch: Dispatch,
    /** GlobalEventsHandler.refreshId */
    refreshId: number,
    /** GlobalEventsHandler.refreshEvent */
    refreshEvent: ?ServerEventMessage,
    /** whether to show FormBarFactory */
    showHeader?: boolean,
    /** optional DOM ref to render ButtonToolbar onto (otherwise will render as child of form) */
    buttonToolbarPortal?: React.DOMElement,
    /** whether no data is intentional or an error */
    isNoDataError?: boolean,
    /** persisted bool if original filename should be kept */
    webDavName: WebDavNameProps,
    /** whether to autoSave on pagination */
    autoSave: boolean,
    /** apply form values to all hierarchy items */
    applyToAll: ApplyToAllProps,
};

type State = {
    form?: FormResultDTO | DocFormResultDTO,
    status: ConstantsEnum,
};

export class FormContainer extends PureComponent<Props, State> {
    abortController: AbortController | null;
    _isMounted: boolean;
    _formsyRef: Formsy | null;

    static defaultProps = {
        isEditMode: false,
        isFormButtonEnabled: true,
        isOnline: true,
        isOfflineAllowed: false,
        showHeader: true,
        autoSave: false,
    };

    constructor(props: Props) {
        super(props);
        this.state = {
            form: undefined,
            status: Constants.LOADING,
        };
    }

    componentDidMount = () => {
        this._isMounted = true;
        this._fetch(this.props.itemUri);
    };

    componentWillUnmount() {
        this._isMounted = false;
        /* istanbul ignore else */
        if (this.abortController != null) {
            this.abortController.abort();
        }
    }

    componentDidUpdate(prevProps: Props, prevState: State) {
        /* istanbul ignore else */
        if (
            prevProps.itemUri !== this.props.itemUri ||
            prevProps.refreshId !== this.props.refreshId ||
            prevProps.form !== this.props.form
        ) {
            this.setState({
                status: Constants.LOADING,
            });
            this._fetch(this.props.itemUri);
            return;
        }
    }

    /**
     * @async
     * @summary will fetch the appropriate form (new/edit) from the server
     * @param {string} paramUri - the itemUri of the item
     * @memberof FormContainer
     */
    _fetch = async (paramUri: string) => {
        this._updateToggleLoading(true);
        try {
            let form = this.props.form;
            if (form == null) {
                if (this.props.newFormType != null) {
                    form = await this.props.offlineHandler.factory(
                        fetchNewForm
                    )(this.props.newFormType, paramUri, this.props.fileId);
                } else {
                    form = await this.props.offlineHandler.factory(fetchForm)(
                        paramUri
                    );
                }
                if (typeof this.props.onFormLoad === "function")
                    this.props.onFormLoad(form);
            }
            /* istanbul ignore else */
            if (
                form != null &&
                this.props.itemUri === paramUri &&
                this._isMounted
            ) {
                this.setState({
                    status: Constants.OK,
                    form,
                });
            }
        } catch (e) {
            /* istanbul ignore else */
            if (e.constructor !== ApiForbiddenError) {
                console.error(e);
            }
            try {
                this.setState({ status: Constants.ERROR });
            } catch (e2) {}
        } finally {
            this._updateToggleLoading(false);
        }
    };

    _updateToggleLoading = (loading?: boolean) => {
        const { viewName, dispatch } = this.props;
        dispatch(
            togglesUpdateCounterAction({
                name: viewName,
                counts: { form: 0 },
                status: {
                    form: loading === true ? Constants.LOADING : null,
                },
            })
        );
    };

    /**
     * Will validate the model against the server
     * @async
     * @param {Object} model contains current changed fields & attributes
     * @returns {Object} containing all localized validation errors per field e.g. {fieldName:error, fieldName:error...}
     * @memberof FormContainer
     */
    _onValidate = async (model: Object): Object => {
        this.abortController = new AbortController();
        const validationErrors = await validateForm(
            this.props.newFormType != null, //isNew
            this.props.form?.isDoc ?? this.state.form?.isDoc,
            this.props.itemUri,
            !(this.props.webDavName?.ignore ?? false),
            model,
            this.abortController.signal
        );
        // localize error messages
        const locErrors = {};
        if (!isEmpty(validationErrors)) {
            Object.keys(validationErrors).forEach(
                (k) => (locErrors[k] = getLocalizedText(validationErrors[k]))
            );
        }
        return locErrors;
    };

    /**
     * Will submit the form to the server
     * @returns {boolean} true if success, false otherwise
     * @async
     * @param {Object} model - contains current changed fields & attributes
     * @memberof FormContainer
     */
    _onSave = async (
        model: Object,
        cancelEditMode: boolean = true
    ): boolean => {
        try {
            let response: boolean | null | ApiError = null;
            if (this.props.newFormType != null) {
                this.props.rememberValues?.updateAvailableValues(model);
                return await this.props.onSaveNew(
                    model,
                    this.props.webDavName?.ignore ?? false,
                    this.state.form.formatId,
                    this.props.applyToAll?.value ?? false
                );
            } else {
                response = await saveForm(
                    this.props.itemUri,
                    this.state.form.formatId,
                    model,
                    this.state.form.etag
                );
            }
            /* istanbul ignore else */
            if (response != null) {
                if (response === false || response.status > 299) {
                    toastActionResult(
                        false,
                        `${this.props.viewName}:edit_form`
                    );
                } else {
                    toastActionResult(true, `${this.props.viewName}:edit_form`);
                    cancelEditMode && this.props.onCancel();
                    return true;
                }
            }
        } catch (e) {
            if (e.data && e.constructor === ApiConflictError) {
                const doOverwrite = await openConflictModal(
                    e.data.extensions.extra.name
                );
                if (doOverwrite) {
                    const result = await new Promise((resolve) => {
                        this.setState(
                            {
                                form: produce(
                                    this.state.form,
                                    (draft: FormResultDTO) => {
                                        draft.etag =
                                            e.data.extensions.extra.currentEtag;
                                    }
                                ),
                            },
                            async () => {
                                const saveResult = await this._onSave(model);
                                resolve(saveResult);
                            }
                        );
                    });
                    return result;
                } else {
                    cancelEditMode && this.props.onCancel();
                }
            } else {
                toastActionResult(false, `${this.props.viewName}:edit_form`);
            }
        }
        return false;
    };

    renderNewDocument = () => {
        if (this.props.newFormType !== NewFormType.Document) {
            return null;
        }
        return (
            <>
                {this.props.webDavName?.renderControl([
                    styles.newDoc_group,
                    styles.newDoc_checkbox,
                    styles.newDoc_label,
                ])}
                {this.props.rememberValues?.renderControl([
                    styles.newDoc_group,
                    styles.newDoc_checkbox,
                    styles.newDoc_label,
                ])}
                {this.props.applyToAll?.renderControl([
                    styles.newDoc_group,
                    styles.newDoc_checkbox,
                    styles.newDoc_label,
                ])}
            </>
        );
    };

    getFormBarViewNames = () =>
        this.props.showHeader ? [this.props.viewName] : [];

    render() {
        const {
            buttonToolbarPortal,
            defaultValues,
            isEditMode,
            isFormButtonEnabled,
            isNoDataError,
            isOfflineAllowed,
            isOnline,
            itemUri,
            newFormType,
            offlineHandler,
            onBack,
            onCancel,
            onSaveReturnAllFields,
            saveLabel,
            savingLabel,
            paginationEvent,
            autoSave,
        } = this.props;
        const { status, form } = this.state;
        let adjustedItemUri = itemUri;
        //TODO: Move to Server
        if (newFormType === NewFormType.Document) {
            adjustedItemUri +=
                "@eid=00000000|000|000|0000000000000000000000000";
        }
        if (status === Constants.OK) {
            return (
                <>
                    <FormBarFactory
                        className={styles.header}
                        views={this.getFormBarViewNames()}
                        isEdit={isEditMode}
                        onCancel={onCancel}
                        onBack={onBack}
                        title={
                            form.formTitle ||
                            form.name ||
                            this.props.defaultFormTitle
                        }
                        icon={this.props.defaultFormIcon}
                        children={this.renderNewDocument()}
                        lockInfo={form.lockInfo}
                    />
                    {window.CONFIG.licenseAllowEdit && isEditMode ? (
                        <FormEdit
                            key={`${itemUri}-formedit`}
                            form={form}
                            paginationEvent={paginationEvent}
                            autoSave={autoSave}
                            itemUri={adjustedItemUri}
                            onValidate={this._onValidate}
                            onCancel={onBack || onCancel}
                            onSave={this._onSave}
                            saveLabel={saveLabel}
                            savingLabel={savingLabel}
                            confirmLabel={
                                /* istanbul ignore next */
                                newFormType === NewFormType.Document
                                    ? null
                                    : loc.t("common:confirm.leaveEdit")
                            }
                            isFormButtonEnabled={
                                isFormButtonEnabled &&
                                (isOnline || isOfflineAllowed)
                            }
                            onSaveReturnAllFields={onSaveReturnAllFields}
                            defaultValues={defaultValues}
                            buttonToolbarPortal={buttonToolbarPortal}
                        />
                    ) : (
                        <Form
                            key={`form-${itemUri}`}
                            form={form}
                            itemUri={itemUri}
                            isNoDataError={isNoDataError}
                        />
                    )}
                </>
            );
        } else
            return (
                <Status status={status} error={offlineHandler.error} center />
            );
    }
}

const mapStateToProps = /* istanbul ignore next */ (state, ownProps) => ({
    isOnline: s.isOnlineSelector(state),
    isOfflineAllowed: isFeatureOn(Feature.offline),
    ...ownProps,
});

export default compose(
    connect(mapStateToProps),
    withOfflineHandler,
    withRememberValues,
    withWebDavName,
    withApplyToAll,
    GlobalEventsHandler({
        actions: [
            ServerEventActionType.modify,
            ServerEventActionType.edit,
            ServerEventActionType.refresh,
        ],
        items: [ServerEventItemType.document, ServerEventItemType.folder],
        refreshMode: RefreshMode.Originator,
        refreshMessage: "$globalEvents:refresh.form",
    })
)(FormContainer);
